import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { finalize, takeUntil } from 'rxjs/operators';

import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { DataImportEntityGroupEnum, DataImportResponseModel } from 'projects/workspace/src/app/data-import/models';
import { DataImportApiService } from 'projects/workspace/src/app/data-import/data-import-api.service';
import { DataImportModalComponent } from '../data-import-modal/data-import-modal.component';

@Component({
  selector: 'rnpl-data-import-payments-modal',
  templateUrl: './data-import-payments-modal.component.html',
  styleUrls: ['./data-import-payments-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataImportPaymentsModalComponent implements OnDestroy {

  public dataImportEntityGroupEnum = DataImportEntityGroupEnum;
  @ViewChild('UploadFileInput', {static: true}) public uploadFileInputEl: ElementRef;

  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  readonly uploadingProcessing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private dialog: MatDialog,
    private dataImportApiService: DataImportApiService,
    private toasterService: ToasterService,
    private translateService: TranslateService,
    public dialogRef: MatDialogRef<DataImportPaymentsModalComponent>,
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  public uploadFile(filesList: FileList): void {
    if (!filesList || !filesList.length) { return; }
    if (filesList.length > 1) {
      this.toasterService.notify({type: 'error', message: 'DATA_IMPORT.SINGLE_FILE_ERROR'});
      return;
    }

    const file: File = filesList[0];
    const availableFormats = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/xml',
    ];

    if (!availableFormats.includes(file.type)) {
      this.toasterService.notify({type: 'error', message: 'DATA_IMPORT.FILE_TYPE_ERROR'});
      return;
    }

    this.uploadFileInputEl.nativeElement.value = null;
    this.postUploadedFile(file);
  }

  public postUploadedFile(file: File): void {
    if (this.uploadingProcessing.getValue()) {
      return;
    }

    this.uploadingProcessing.next(true);

    this.toasterService.notifyRequestMessage({key: 'importedFileProcessing', message: 'DATA_IMPORT.FILE_PROCESSING'});

    this.dataImportApiService.importPayments(file)
      .pipe(
        finalize(() => {
          this.uploadingProcessing.next(false);
          this.toasterService.hideRequestMessage('importedFileProcessing');
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response: DataImportResponseModel) => {
        if (response.readyCount && !response.errorsCount) {
          const msg = this.translateService.instant('DATA_IMPORT.PAYMENTS_IMPORT_SUCCESS', {count: response.readyCount});
          this.toasterService.notifySuccess(msg);
          this.dialogRef.close(true);
          return;
        }

        this.dialog.open(DataImportModalComponent, {
          data: {
            currentImportEntityGroup: DataImportEntityGroupEnum.PAYMENTS,
            validationResponse: response,
            uploadedFile: file
          },
          backdropClass: 'pointer-events-none',
          panelClass: ['max-h-100', 'radius-16'],
          width: 'calc(100% - 8px)',
          maxWidth: '100%',
          height: 'calc(100% - 4px)',
          position: {
            top: '48px'
          },
          disableClose: true
        }).afterClosed()
          .subscribe(res => {
            this.dialogRef.close(res);
          });
      }, error => {
        if (error.error && error.error.message) {
          this.toasterService.notifyError(error.error.message);
        }
        if (error.error && error.error.errors) {
          this.toasterService.notifyError(error.error.errors);
        }
      });
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
